
import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { 
    Calendar as CalendarIcon, 
    ChevronLeft, 
    ChevronRight, 
    Plus,
    Clock,
    MapPin,
    Users,
    X,
    ChevronRight as ChevronRightIcon,
    Edit2
} from 'lucide-react';

const CalendarManager: React.FC = () => {
    const { theme } = useOutletContext<{ theme: 'light' | 'dark' }>();
    const isDark = theme === 'dark';

    const days = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

    const [currentDate, setCurrentDate] = useState(new Date());

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth(); // 0-11

    // Get the name of the month and year in Spanish
    const monthName = currentDate.toLocaleString('es-ES', { month: 'long' });
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
    const monthYearLabel = `${capitalizedMonth} ${year}`;

    // Get number of days in the month
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Get the day of the week the first day of the month falls on (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    // In Spanish, the week starts on Monday, so we adjust it:
    // Sunday (0) -> 6, Monday (1) -> 0, Tuesday (2) -> 1, etc.
    let startDayOffset = new Date(year, month, 1).getDay();
    startDayOffset = startDayOffset === 0 ? 6 : startDayOffset - 1;

    const prevMonth = () => {
        setCurrentDate(new Date(year, month - 1, 1));
    };

    const nextMonth = () => {
        setCurrentDate(new Date(year, month + 1, 1));
    };

    const today = new Date();
    const currentYearVal = today.getFullYear();
    const currentMonthVal = today.getMonth();

    // Mock events associated with the current month/year
    const [events] = useState([
        { id: '1', year: currentYearVal, month: currentMonthVal, day: 15, name: 'Laguna Cerro Castillo', type: 'trekking', spots: '4/6', color: '#00d4ff', time: '08:30 AM', location: 'Hotel Coyhaique', guide: 'Jose Alarcón' },
        { id: '1b', year: currentYearVal, month: currentMonthVal, day: 15, name: 'Escalada en Roca', type: 'climbing', spots: '2/4', color: '#f2f2f2', time: '10:00 AM', location: 'Paredones', guide: 'Matias Ruiz' },
        { id: '2', year: currentYearVal, month: currentMonthVal, day: 16, name: 'Laguna Cerro Castillo', type: 'trekking', spots: '2/6', color: '#00d4ff', time: '08:30 AM', location: 'Hotel Coyhaique', guide: 'Jose Alarcón' },
        { id: '3', year: currentYearVal, month: currentMonthVal, day: 20, name: 'Escalada Hielo', type: 'climbing', spots: '1/4', color: isDark ? '#f2f2f2' : '#444', time: '07:00 AM', location: 'Exploradores', guide: 'Guía Local' },
        { id: '4', year: currentYearVal, month: currentMonthVal, day: 22, name: 'Wellness Day', type: 'wellness', spots: 'Full', color: '#25D366', time: '09:00 AM', location: 'Spa Cerro Castillo', guide: 'Ana Maria' },
    ]);

    const [selectedEvent, setSelectedEvent] = useState<any>(events[0]);
    const [isGroupDrawerOpen, setIsGroupDrawerOpen] = useState(false);

    const cardBg = isDark ? 'bg-[#1a1512]' : 'bg-white';
    const borderColor = isDark ? 'border-rock' : 'border-gray-100';
    const textColor = isDark ? 'text-white' : 'text-gray-900';
    const subTextColor = isDark ? 'text-gray-400' : 'text-gray-500';

    // Mock detailed participants
    const participants = [
        { id: 1, name: 'Alice Johnson', status: 'OK', passport: 'A1234567', country: 'USA', food: 'Vegetariana', medical: 'Ninguna', waiver: true, phone: '+1234567890' },
        { id: 2, name: 'Mark Ruffalo', status: 'Pendiente Pago', passport: 'B9876543', country: 'USA', food: 'Sin Gluten', medical: 'Asma sutil', waiver: true, phone: '+1987654321' },
        { id: 3, name: 'Sarah Connor', status: 'OK', passport: 'C5554443', country: 'UK', food: 'Ninguna', medical: 'Ninguna', waiver: false, phone: '+4477889900' },
    ];

    return (
        <div className="relative">
            {/* Main Content */}
            <div className={`space-y-8 transition-all duration-500 ${isGroupDrawerOpen ? 'blur-md' : ''}`}>
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className={`text-4xl font-title uppercase tracking-tighter ${textColor}`}>Calendario de Salidas</h1>
                        <p className={`${subTextColor} text-[10px] uppercase tracking-[0.3em] font-bold mt-1`}>Gestiona las fechas activas y el estado de los grupos.</p>
                    </div>
                    <button className={`px-8 py-4 font-title text-lg uppercase tracking-widest flex items-center gap-3 transition-all rounded-[10px] shadow-lg ${
                        isDark ? 'bg-white text-black hover:bg-[#00d4ff]' : 'bg-gray-900 text-white hover:bg-[#00d4ff]'
                    }`}>
                        <Plus className="w-5 h-5" />
                        Programar Salida
                    </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Calendar Grid */}
                    <div className={`lg:col-span-2 ${cardBg} border ${borderColor} p-8 rounded-[10px] shadow-sm`}>
                        <div className="flex items-center justify-between mb-8">
                            <h3 className={`text-2xl font-title uppercase tracking-tighter ${textColor}`}>{monthYearLabel}</h3>
                            <div className="flex gap-2">
                                <button 
                                    onClick={prevMonth}
                                    className={`p-2 border ${borderColor} transition-all rounded-[10px] ${isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-50 text-gray-500'}`}
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <button 
                                    onClick={nextMonth}
                                    className={`p-2 border ${borderColor} transition-all rounded-[10px] ${isDark ? 'hover:bg-white/5 text-gray-400' : 'hover:bg-gray-50 text-gray-500'}`}
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <div className={`grid grid-cols-7 gap-px overflow-hidden rounded-[10px] border ${borderColor} ${isDark ? 'bg-rock/30' : 'bg-gray-100'}`}>
                            {days.map(day => (
                                <div key={day} className={`${isDark ? 'bg-black' : 'bg-gray-50/50'} py-4 text-center text-[10px] font-bold uppercase tracking-widest text-gray-500 border-b ${borderColor}`}>
                                    {day}
                                </div>
                            ))}
                            {/* Empty cells for offset */}
                            {Array.from({ length: startDayOffset }).map((_, i) => (
                                <div key={`empty-${i}`} className={`${isDark ? 'bg-black/10' : 'bg-gray-50/5'} min-h-[120px] border-b ${borderColor} opacity-30`} />
                            ))}

                            {/* Actual days in month */}
                            {Array.from({ length: daysInMonth }).map((_, i) => {
                                const day = i + 1;
                                const dayEvents = events.filter(e => e.year === year && e.month === month && e.day === day);
                                return (
                                    <div 
                                        key={i} 
                                        className={`${isDark ? 'bg-black' : 'bg-white'} min-h-[120px] h-auto p-3 transition-all group relative border-b ${borderColor}`}
                                    >
                                        <span className={`text-xs font-bold mb-2 block ${dayEvents.length > 0 ? 'text-[#00d4ff]' : isDark ? 'text-gray-700' : 'text-gray-300'}`}>{day}</span>
                                        <div className="space-y-1.5">
                                            {dayEvents.map((event) => {
                                                const isSelected = selectedEvent?.id === event.id;
                                                return (
                                                    <div 
                                                        key={event.id}
                                                        onClick={() => setSelectedEvent(event)}
                                                        className={`p-2 text-[8px] font-black uppercase tracking-widest border-l-4 leading-tight rounded-r-[10px] transition-all cursor-pointer ${
                                                            isSelected 
                                                            ? 'ring-2 ring-[#00d4ff]/40 shadow-lg scale-[1.02] translate-x-1' 
                                                            : 'hover:translate-x-1 hover:bg-black/5'
                                                        }`}
                                                        style={{ 
                                                            borderColor: event.color, 
                                                            backgroundColor: isSelected ? `${event.color}30` : `${event.color}${isDark ? '15' : '20'}`, 
                                                            color: isDark 
                                                                ? (isSelected ? '#fff' : event.color) 
                                                                : (event.color === '#f2f2f2' ? '#1a1a1a' : 
                                                                   event.color === '#00d4ff' ? '#008bb3' : 
                                                                   event.color === '#25D366' ? '#1a9a4b' : event.color)
                                                        }}
                                                    >
                                                        {event.name}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Event Sidebar */}
                    <div className="space-y-6">
                        <h3 className={`text-xl font-title uppercase tracking-widest ${textColor} flex items-center gap-3`}>
                            <span className="w-2 h-2 bg-[#00d4ff] block"></span>
                            Detalle de Salida
                        </h3>
                        
                        {selectedEvent ? (
                            <div className={`${cardBg} border ${isDark ? 'border-[#00d4ff]/30' : 'border-[#00d4ff]/20'} p-8 space-y-8 rounded-[10px] shadow-sm animate-in slide-in-from-right duration-500`}>
                                <div className="space-y-2">
                                    <p className="text-[10px] text-[#00d4ff] font-black uppercase tracking-widest">
                                        Día {selectedEvent.day} de {
                                            new Date(selectedEvent.year, selectedEvent.month, 1)
                                                .toLocaleString('es-ES', { month: 'long' })
                                                .replace(/^\w/, (c) => c.toUpperCase())
                                        } de {selectedEvent.year}
                                    </p>
                                    <h4 className={`text-2xl font-title ${textColor} uppercase tracking-tight`}>{selectedEvent.name}</h4>
                                </div>

                                <div className="space-y-4">
                                    <div className="flex items-center gap-4">
                                        <Clock className="w-4 h-4 text-[#00d4ff]" />
                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${subTextColor}`}>{selectedEvent.time} - Salida</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <MapPin className="w-4 h-4 text-[#00d4ff]" />
                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${subTextColor}`}>Pick-up: {selectedEvent.location}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <Users className="w-4 h-4 text-[#00d4ff]" />
                                        <span className={`text-[10px] font-bold uppercase tracking-widest ${subTextColor}`}>Guía: {selectedEvent.guide}</span>
                                    </div>
                                </div>

                                <div className={`pt-8 border-t ${borderColor}`}>
                                    <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest mb-4">Estado del Grupo ({selectedEvent.spots})</p>
                                    <div className="space-y-2">
                                        {participants.map((p, i) => (
                                            <div key={i} className={`flex items-center justify-between text-[10px] font-bold uppercase tracking-widest p-3 rounded-[10px] ${
                                                isDark ? 'text-white bg-black/40' : 'text-gray-900 bg-gray-50'
                                            }`}>
                                                <span>{p.name}</span>
                                                <span className={p.status === 'OK' ? 'text-[#25D366]' : 'text-orange-500'}>{p.status}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <button 
                                    onClick={() => setIsGroupDrawerOpen(true)}
                                    className={`w-full py-5 border ${borderColor} text-[10px] font-black uppercase tracking-widest transition-all rounded-[10px] hover:bg-[#00d4ff] hover:text-black hover:border-[#00d4ff] shadow-md ${textColor}`}
                                >
                                    Gestionar Grupo
                                </button>
                            </div>
                        ) : (
                            <div className={`p-12 text-center border-2 border-dashed ${borderColor} rounded-[10px] opacity-30`}>
                                <p className="text-[10px] font-bold uppercase tracking-widest">Selecciona una actividad</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Slide-over Drawer - Fixed and simplified */}
            {isGroupDrawerOpen && createPortal(
                <div className="fixed inset-0 z-[10000] flex justify-end overflow-hidden">
                    {/* Darker Backdrop with blur */}
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-300" onClick={() => setIsGroupDrawerOpen(false)} />
                    
                    {/* Drawer Panel */}
                    <div className={`relative w-full max-w-2xl h-full shadow-2xl border-l ${borderColor} flex flex-col transform transition-transform duration-500 ease-out translate-x-0 ${
                        isDark ? 'bg-[#1a1512] text-white' : 'bg-white text-gray-900'
                    } rounded-l-[10px]`}>
                        
                        {/* Drawer Header */}
                        <div className="p-10 border-b border-[#00d4ff]/10 flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 bg-[#00d4ff]/10 rounded-[10px] flex items-center justify-center">
                                    <Users className="w-8 h-8 text-[#00d4ff]" />
                                </div>
                                <div>
                                    <h2 className="text-3xl font-title uppercase tracking-tighter">Manifiesto de Grupo</h2>
                                    <p className="text-[10px] text-[#00d4ff] font-bold uppercase tracking-[0.3em]">{selectedEvent.name} • Día {selectedEvent.day}</p>
                                </div>
                            </div>
                            <button onClick={() => setIsGroupDrawerOpen(false)} className="p-4 hover:bg-red-500/10 rounded-full transition-all group">
                                <X className="w-6 h-6 text-gray-400 group-hover:text-red-500" />
                            </button>
                        </div>

                        {/* Drawer Content */}
                        <div className="flex-1 overflow-y-auto p-10 space-y-10">
                            {/* Logistics Summary */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className={`p-6 rounded-[10px] border ${borderColor} ${isDark ? 'bg-black/40' : 'bg-gray-50'}`}>
                                    <p className="text-[8px] text-gray-500 font-bold uppercase tracking-widest mb-2">Guía Asignado</p>
                                    <div className="flex items-center justify-between">
                                        <span className="font-title uppercase tracking-tight">{selectedEvent.guide}</span>
                                        <button className="text-[8px] text-[#00d4ff] font-bold border border-[#00d4ff]/30 px-2 py-1 rounded-[10px] hover:bg-[#00d4ff]/10 transition-all">CAMBIAR</button>
                                    </div>
                                </div>
                                <div className={`p-6 rounded-[10px] border ${borderColor} ${isDark ? 'bg-black/40' : 'bg-gray-50'}`}>
                                    <p className="text-[8px] text-gray-500 font-bold uppercase tracking-widest mb-2">Cupos Ocupados</p>
                                    <div className="flex items-center gap-2">
                                        <span className="text-2xl font-title text-[#00d4ff]">{selectedEvent.spots}</span>
                                        <span className="text-[10px] text-gray-500 font-bold">PASAJEROS</span>
                                    </div>
                                </div>
                            </div>

                            {/* Participants List */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between px-2">
                                    <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">Pasajeros Confirmados</h3>
                                    <span className="text-[10px] font-bold text-[#25D366] bg-[#25D366]/10 px-2 py-1 rounded-md uppercase">Todos OK</span>
                                </div>
                                
                                <div className="space-y-4">
                                    {participants.map((p) => (
                                        <div key={p.id} className={`p-8 rounded-[10px] border transition-all hover:border-[#00d4ff]/40 group ${
                                            isDark ? 'bg-black/20 border-rock' : 'bg-white border-gray-100 shadow-sm'
                                        }`}>
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                                <div className="flex items-center gap-5">
                                                    <div className="w-12 h-12 bg-gray-500/10 rounded-[10px] flex items-center justify-center font-title text-xl text-gray-400 group-hover:text-[#00d4ff] transition-colors">
                                                        {p.name.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <h4 className="text-xl font-title uppercase tracking-tight">{p.name}</h4>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <span className="text-[9px] text-gray-500 font-bold">{p.country}</span>
                                                            <span className="w-1 h-1 bg-gray-700 rounded-full"></span>
                                                            <span className="text-[9px] text-gray-500 font-bold">Pasaporte: {p.passport}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-2">
                                                    <button className="p-3 bg-[#25D366]/10 text-[#25D366] rounded-[10px] hover:bg-[#25D366]/20 transition-all shadow-lg shadow-[#25D366]/5" title="WhatsApp">
                                                        <div className="flex items-center gap-2">
                                                            <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.72.94 3.675 1.439 5.662 1.439h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                                                            <span className="text-[9px] font-black uppercase">Chatear</span>
                                                        </div>
                                                    </button>
                                                    <button className={`p-3 border rounded-[10px] transition-all ${isDark ? 'border-rock bg-black hover:bg-white hover:text-black' : 'border-gray-100 bg-gray-50 hover:bg-gray-900 hover:text-white'}`}>
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                </div>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-dashed border-[#00d4ff]/10">
                                                <div>
                                                    <p className="text-[8px] text-gray-500 font-bold uppercase tracking-widest mb-1">Comida</p>
                                                    <p className="text-[10px] font-bold text-gray-400">{p.food}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[8px] text-gray-500 font-bold uppercase tracking-widest mb-1">Médico</p>
                                                    <p className={`text-[10px] font-bold ${p.medical !== 'Ninguna' ? 'text-red-400' : 'text-gray-400'}`}>{p.medical}</p>
                                                </div>
                                                <div>
                                                    <p className="text-[8px] text-gray-500 font-bold uppercase tracking-widest mb-1">Deslinde</p>
                                                    <span className={`text-[8px] font-black uppercase ${p.waiver ? 'text-[#25D366]' : 'text-orange-500'}`}>{p.waiver ? 'FIRMADO' : 'PENDIENTE'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Drawer Footer */}
                        <div className={`p-10 border-t ${borderColor} flex items-center justify-between shrink-0`}>
                            <button className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest ${subTextColor} hover:text-[#00d4ff] transition-colors`}>
                                <Plus className="w-4 h-4" />
                                Añadir Pasajero
                            </button>
                            <button onClick={() => setIsGroupDrawerOpen(false)} className="bg-[#00d4ff] text-black px-10 py-5 rounded-[10px] font-title text-lg uppercase tracking-widest hover:scale-105 transition-all shadow-xl shadow-[#00d4ff]/20">
                                Cerrar Manifiesto
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default CalendarManager;
